import React from "react"
import dynamic from "next/dynamic"

import { BarChart3, ListChecks, TrendingUp } from "lucide-react"

import {
  chartSeriesColors,
  lineSeriesColors,
} from "@/components/charts/seriesPalette"
import { BUCKET_INTERVAL } from "@/utils/date/timeBucketing"

import { useChartWidgetData } from "../../hooks/chart/useChartWidgetData"
import type {
  ChartClickData,
  ChartWidgetConfig,
  WidgetDimensions,
} from "../../types/chart"
import {
  AGGREGATION_TYPE,
  DATA_SOURCE,
  VISUALIZATION_TYPE,
} from "../../types/core"
import { buildChartLevelDrillDownClickData } from "../../utils/chart/chartLevelDrillDown"
import { getDashboardWidgetChartSyncId } from "../../utils/chart/chartSync"
import {
  parseMetadataGroupKey,
  parseSplitKey,
} from "../../utils/chart/splitKeys"
import {
  getResponsiveContainerWidth,
  getScaledDimensions,
  GRID_COLUMNS,
} from "../../utils/gridMetrics"
import {
  BaseWidget,
  type WidgetExportData,
  type WidgetProps,
} from "../BaseWidget"
import { ChartWidgetSkeleton } from "../ChartWidgetSkeleton"

const LineChartVisualization = dynamic(
  () =>
    import("./visualizations/LineChartVisualization").then(
      (mod) => mod.LineChartVisualization,
    ),
  { ssr: false },
)

const BarChartVisualization = dynamic(
  () =>
    import("./visualizations/BarChartVisualization").then(
      (mod) => mod.BarChartVisualization,
    ),
  { ssr: false },
)

const AreaChartVisualization = dynamic(
  () =>
    import("./visualizations/AreaChartVisualization").then(
      (mod) => mod.AreaChartVisualization,
    ),
  { ssr: false },
)

const StatisticVisualization = dynamic(
  () =>
    import("./visualizations/StatisticVisualization").then(
      (mod) => mod.StatisticVisualization,
    ),
  { ssr: false },
)

const PieChartVisualization = dynamic(
  () =>
    import("./visualizations/PieChartVisualization").then(
      (mod) => mod.PieChartVisualization,
    ),
  { ssr: false },
)

const HistogramVisualization = dynamic(
  () =>
    import("./visualizations/HistogramVisualization").then(
      (mod) => mod.HistogramVisualization,
    ),
  { ssr: false },
)

const TopListVisualization = dynamic(
  () =>
    import("./visualizations/TopListVisualization").then(
      (mod) => mod.TopListVisualization,
    ),
  { ssr: false },
)

const TextVisualization = dynamic(
  () =>
    import("./visualizations/TextVisualization").then(
      (mod) => mod.TextVisualization,
    ),
  { ssr: false },
)

// Chart visualization registry
const CHART_VISUALIZATIONS = {
  line: LineChartVisualization,
  bar: BarChartVisualization,
  area: AreaChartVisualization,
  statistic: StatisticVisualization,
  pie: PieChartVisualization,
  histogram: HistogramVisualization,
  "top-list": TopListVisualization,
  text: TextVisualization,
} as const

const CHART_CLICK_DRAG_THRESHOLD_PX = 5
const CHART_CLICK_CONTROL_SELECTOR = [
  "button",
  "a",
  "input",
  "select",
  "textarea",
  '[role="button"]',
  "[data-dashboard-chart-control]",
  ".recharts-legend-wrapper",
].join(",")

function isChartControlClickTarget(target: EventTarget | null): boolean {
  return (
    target instanceof Element &&
    Boolean(target.closest(CHART_CLICK_CONTROL_SELECTOR))
  )
}

interface ChartWidgetContentProps {
  config: ChartWidgetConfig
  onPointClick?: (point: ChartClickData) => void
  onChartClick?: (point: ChartClickData) => void
  onSplitKeysChange?: (splitKeys: string[]) => void
  onConfigChange?: (newConfig: ChartWidgetConfig) => void
  onExportDataReady?: (data: WidgetExportData | null) => void
  widgetDimensions?: WidgetDimensions
  chartSyncId: string
}

const ChartWidgetContent: React.FC<ChartWidgetContentProps> = ({
  config,
  onChartClick,
  onPointClick,
  onSplitKeysChange,
  onConfigChange,
  onExportDataReady,
  widgetDimensions,
  chartSyncId,
}) => {
  const chartClickStartRef = React.useRef<{
    clientX: number
    clientY: number
  } | null>(null)
  const suppressChartClickRef = React.useRef(false)
  const {
    chartData,
    data,
    error,
    handleChartZoom,
    isLoading,
    isMetricLoading,
    lookups,
    lookupsLoading,
    metric,
    showSuccessCriteriaError,
    splitKeys,
    stableDateRange,
    baselineData,
    statisticData,
  } = useChartWidgetData({
    config,
    onSplitKeysChange,
    onExportDataReady,
  })

  const chartLevelClickData = React.useMemo(
    () =>
      buildChartLevelDrillDownClickData({
        config,
        dateRange: stableDateRange,
      }),
    [config, stableDateRange],
  )

  const handleScopedPointClick = React.useCallback(
    (point: ChartClickData) => {
      suppressChartClickRef.current = true
      globalThis.setTimeout(() => {
        suppressChartClickRef.current = false
      }, 0)
      const metadataGroupKey = point.splitValue
        ? (parseSplitKey(point.splitValue).groupId ?? point.splitValue)
        : undefined
      const metadataGroup = metadataGroupKey
        ? parseMetadataGroupKey(metadataGroupKey)
        : null
      const drillDownMetadataGroup =
        config.groupByMetadataKey &&
        metadataGroup &&
        metadataGroup.value !== null
          ? {
              kind: metadataGroup.kind,
              topValues: metadataGroup.topValues,
              value: metadataGroup.value,
            }
          : null

      onPointClick?.({
        ...point,
        drillDownScope: point.drillDownScope ?? "point",
        ...(config.groupByMetadataKey && drillDownMetadataGroup
          ? {
              groupByMetadataKey: config.groupByMetadataKey,
              metadataGroupKind: drillDownMetadataGroup.kind,
              metadataTopValues: drillDownMetadataGroup.topValues,
              splitBy: undefined,
              splitValue: drillDownMetadataGroup.value,
            }
          : {}),
      })
    },
    [config.groupByMetadataKey, onPointClick],
  )

  const handleScopedChartClick = React.useCallback(
    (point: ChartClickData) => {
      suppressChartClickRef.current = true
      globalThis.setTimeout(() => {
        suppressChartClickRef.current = false
      }, 0)
      onChartClick?.(point)
    },
    [onChartClick],
  )

  const handleChartPointerDown = React.useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (event.button !== 0) {
        chartClickStartRef.current = null
        return
      }

      chartClickStartRef.current = {
        clientX: event.clientX,
        clientY: event.clientY,
      }
    },
    [],
  )

  const handleChartSurfaceClick = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!onChartClick || !chartLevelClickData || event.defaultPrevented) {
        return
      }

      if (isChartControlClickTarget(event.target)) {
        return
      }

      if (suppressChartClickRef.current) {
        suppressChartClickRef.current = false
        return
      }

      const clickStart = chartClickStartRef.current
      chartClickStartRef.current = null
      if (clickStart) {
        const distance = Math.hypot(
          event.clientX - clickStart.clientX,
          event.clientY - clickStart.clientY,
        )
        if (distance > CHART_CLICK_DRAG_THRESHOLD_PX) {
          return
        }
      }

      onChartClick(chartLevelClickData)
    },
    [chartLevelClickData, onChartClick],
  )

  if (showSuccessCriteriaError) {
    return (
      <div className="flex items-center justify-center h-full text-coval-text-muted">
        <div className="text-center">
          <ListChecks className="w-8 h-8 mx-auto mb-2" />
          <p>Success criteria is required for success rate</p>
        </div>
      </div>
    )
  }

  if (isLoading || lookupsLoading || isMetricLoading) {
    return <ChartWidgetSkeleton />
  }

  // Only show error state when the query actually failed (never on refetch or transient !data)
  if (error) {
    return (
      <div className="flex items-center justify-center h-full text-coval-text-muted">
        <div className="text-center">
          <TrendingUp className="w-8 h-8 mx-auto mb-2" />
          <p>Error loading data</p>
        </div>
      </div>
    )
  }

  if (!data || chartData.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-coval-text-muted">
        <div className="text-center">
          <BarChart3 className="w-8 h-8 mx-auto mb-2" />
          <p>No Metric Data Available</p>
        </div>
      </div>
    )
  }

  const VisualizationComponent = CHART_VISUALIZATIONS[config.visualizationType]

  if (!VisualizationComponent) {
    return (
      <div className="flex items-center justify-center h-full text-coval-text-muted">
        <div className="text-center">
          <TrendingUp className="w-8 h-8 mx-auto mb-2" />
          <p>Unsupported visualization type: {config.visualizationType}</p>
        </div>
      </div>
    )
  }

  return (
    <div
      className="h-full w-full"
      onPointerDown={handleChartPointerDown}
      onClick={handleChartSurfaceClick}
    >
      <VisualizationComponent
        metric={metric}
        chartData={chartData}
        config={config}
        splitKeys={splitKeys}
        colors={
          config.visualizationType === VISUALIZATION_TYPE.LINE
            ? lineSeriesColors(splitKeys?.length || 8)
            : chartSeriesColors(splitKeys?.length || 8)
        }
        onPointClick={handleScopedPointClick}
        onChartClick={handleScopedChartClick}
        widgetDimensions={widgetDimensions}
        displayNameLookups={lookups}
        onConfigChange={onConfigChange}
        statisticData={statisticData}
        baselineData={config.showBaselineBands ? baselineData : null}
        dateRange={stableDateRange}
        onZoom={handleChartZoom}
        chartSyncId={chartSyncId}
      />
    </div>
  )
}

export class ChartWidget extends BaseWidget {
  type = "chart"
  title = "Metric Chart"
  description = "Dynamic metric visualization"
  icon = (<TrendingUp className="w-5 h-5" />)
  disabled = false

  renderWidget(
    props: WidgetProps & {
      onPointClick?: (point: ChartClickData) => void
      onChartClick?: (point: ChartClickData) => void
      onSplitKeysChange?: (splitKeys: string[]) => void
    },
  ): React.ReactNode {
    const config = props.widget.config as ChartWidgetConfig
    const { onChartClick, onPointClick, onSplitKeysChange, onExportDataReady } =
      props

    // Calculate widget dimensions from grid properties
    const gridW = props.widget.grid_w || 12
    const gridH = props.widget.grid_h || 4
    const containerWidth = getResponsiveContainerWidth()
    const scaledDimensions = getScaledDimensions(containerWidth)

    // Calculate pixel dimensions
    const pixelW = (containerWidth / GRID_COLUMNS) * gridW
    const pixelH = scaledDimensions.rowHeight * gridH

    const widgetDimensions: WidgetDimensions = {
      gridW,
      gridH,
      pixelW,
      pixelH,
    }

    if (!config?.metricId || !config?.visualizationType) {
      return (
        <div className="flex items-center justify-center h-full text-coval-text-muted">
          <div className="text-center">
            <TrendingUp className="w-8 h-8 mx-auto mb-2" />
            <p>Configure metric and visualization</p>
          </div>
        </div>
      )
    }

    return (
      <div className="w-full h-full select-none">
        <ChartWidgetContent
          config={config}
          onChartClick={onChartClick}
          onPointClick={onPointClick}
          onSplitKeysChange={onSplitKeysChange}
          onConfigChange={props.widget?.onConfigChange}
          onExportDataReady={onExportDataReady}
          widgetDimensions={widgetDimensions}
          chartSyncId={getDashboardWidgetChartSyncId(props.widget.id)}
        />
      </div>
    )
  }

  getDefaultConfig(): ChartWidgetConfig {
    return {
      title: this.title,
      description: "",
      metricId: "",
      dataSource: DATA_SOURCE.SIMULATED,
      aggregation: AGGREGATION_TYPE.AVG,
      visualizationType: VISUALIZATION_TYPE.LINE,
      bucketInterval: BUCKET_INTERVAL.FOUR_HOURS,
      stacked: false,
      grouped: false,
      showAsPercentage: false,
      splitBy: undefined,
      metricOutputType: "float",
      filters: {},
      xAxisLabel: undefined,
      yAxisLabel: undefined,
    }
  }
}
